import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import { featuredArticles, newReleases, Article } from '@/mocks/movieData';

const allArticles: Article[] = [...featuredArticles, ...newReleases];

function RelatedArticle({ article }: { article: Article }) {
  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer"
    >
      <div className="relative overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full aspect-video object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        {article.badge && (
          <span className="absolute top-2 left-2 px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded whitespace-nowrap">
            {article.badge}
          </span>
        )}
      </div>
      <div className="p-3">
        <h4 className="text-sm font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
          {article.title}
        </h4>
        <p className="text-xs text-foreground-500 line-clamp-2 mt-1 leading-relaxed">
          {article.description}
        </p>
      </div>
    </Link>
  );
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '0', 10);
  const article = allArticles.find((a) => a.id === articleId);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <div className="pt-16">
            <Breadcrumb />
            <div className="max-w-[800px] mx-auto px-4 md:px-6 py-20 text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
              </div>
              <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">内容未找到</h1>
              <p className="text-foreground-500 mb-6">抱歉，您访问的内容不存在或已被移除</p>
              <Link to="/" className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer inline-flex items-center gap-2">
                <i className="ri-home-4-line"></i> 返回首页
              </Link>
            </div>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  const relatedArticles = allArticles
    .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Article" data={{
        headline: article.title,
        description: article.description,
        datePublished: article.date,
        author: { '@type': 'Organization', name: '久久影视' },
        publisher: { '@type': 'Organization', name: '久久影视' },
        inLanguage: 'zh-CN',
        about: article.tags.join(', '),
      }} />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://jiujiuys.com' },
          { '@type': 'ListItem', position: 2, name: article.category, item: `https://jiujiuys.com/category/${article.categorySlug}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb />

          <article className="w-full px-4 md:px-6 py-8 md:py-10">
            <div className="max-w-[800px] mx-auto">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full whitespace-nowrap hover:bg-primary-200 transition-colors cursor-pointer"
                >
                  {article.category}
                </Link>
                {article.badge && (
                  <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full whitespace-nowrap">
                    {article.badge}
                  </span>
                )}
                <span className="flex items-center gap-1 text-accent-600 text-sm font-semibold">
                  <i className="ri-star-fill text-xs"></i>
                  {article.rating}
                </span>
              </div>

              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
                {article.title}
              </h1>

              <div className="flex items-center gap-3 text-sm text-foreground-500 mb-6 flex-wrap">
                <span>{article.date} 发布</span>
                <span>·</span>
                <span>{article.views} 次浏览</span>
              </div>

              <figure className="mb-8 rounded-lg overflow-hidden">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full aspect-video object-cover object-top"
                />
              </figure>

              {article.detail && (
                <>
                  <div className="bg-background-100 rounded-lg p-4 md:p-6 mb-8">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      {article.detail.director && (
                        <div>
                          <span className="text-foreground-400 block text-xs mb-0.5">导演</span>
                          <span className="text-foreground-900 font-medium">{article.detail.director}</span>
                        </div>
                      )}
                      {article.detail.actors && (
                        <div>
                          <span className="text-foreground-400 block text-xs mb-0.5">主演</span>
                          <span className="text-foreground-900 font-medium">{article.detail.actors.join(' / ')}</span>
                        </div>
                      )}
                      {article.detail.year && (
                        <div>
                          <span className="text-foreground-400 block text-xs mb-0.5">年份</span>
                          <span className="text-foreground-900 font-medium">{article.detail.year}</span>
                        </div>
                      )}
                      <div>
                        <span className="text-foreground-400 block text-xs mb-0.5">地区</span>
                        <span className="text-foreground-900 font-medium">{article.detail.region || '中国大陆'}</span>
                      </div>
                      {article.detail.episodes && (
                        <div>
                          <span className="text-foreground-400 block text-xs mb-0.5">集数</span>
                          <span className="text-foreground-900 font-medium">{article.detail.episodes}</span>
                        </div>
                      )}
                      {article.detail.duration && (
                        <div>
                          <span className="text-foreground-400 block text-xs mb-0.5">时长</span>
                          <span className="text-foreground-900 font-medium">{article.detail.duration}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="prose max-w-none mb-8">
                    <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">剧情简介</h2>
                    <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                      {article.detail.content}
                    </p>
                  </div>

                  {article.detail.highlights && article.detail.highlights.length > 0 && (
                    <div className="mb-8">
                      <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">精彩看点</h2>
                      <ul className="flex flex-col gap-2">
                        {article.detail.highlights.map((highlight, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-foreground-700">
                            <i className="ri-checkbox-circle-fill text-accent-500 mt-0.5 shrink-0"></i>
                            {highlight}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}

              <div className="flex items-center gap-2 flex-wrap mb-8">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center justify-between py-4 border-t border-background-200/70 flex-col sm:flex-row gap-3">
                <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer">
                  <i className="ri-arrow-left-line"></i> 返回首页
                </Link>
                <div className="flex items-center gap-4">
                  <button className="text-sm text-foreground-500 hover:text-foreground-700 transition-colors flex items-center gap-1 cursor-pointer">
                    <i className="ri-share-forward-line"></i> 分享
                  </button>
                  <button className="text-sm text-foreground-500 hover:text-foreground-700 transition-colors flex items-center gap-1 cursor-pointer">
                    <i className="ri-bookmark-line"></i> 收藏
                  </button>
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
              <div className="max-w-[1280px] mx-auto">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {relatedArticles.map((related) => (
                    <RelatedArticle key={related.id} article={related} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>
        <Footer />
      </div>
    </>
  );
}